import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ArrowUpRight, Users, LogIn, Mailbox, Check, X, Clock } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { CommandPalette } from "@/components/CommandPalette";
import { FriendPicker } from "@/components/FriendPicker";
import { JoinByCodeDialog } from "@/components/JoinByCodeDialog";
import { api, GROUP_TYPES, CURRENCIES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatDistanceToNowStrict } from "date-fns";

const EMOJIS = ["🏠", "✈️", "🍕", "🎉", "💼", "🎓", "🌊", "🏖️", "🎬", "🎮", "🛍️", "🍻"];

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [cmdkOpen, setCmdkOpen] = useState(false);

  const [name, setName] = useState("");
  const [emoji, setEmoji] = useState("🍕");
  const [type, setType] = useState("custom");
  const [currency, setCurrency] = useState("USD");
  const [pickedFriends, setPickedFriends] = useState([]);
  const [saving, setSaving] = useState(false);
  const [invites, setInvites] = useState({ incoming: [], outgoing: [] });

  const loadInvites = async () => {
    try {
      const { data } = await api.get("/group-invites");
      setInvites(data);
    } catch { /* silent */ }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/groups");
      setGroups(data);
      loadInvites();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const acceptInvite = async (iid) => {
    try {
      const { data } = await api.post(`/group-invites/${iid}/accept`);
      toast.success(`Joined ${data.group_name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const declineInvite = async (iid) => {
    try { await api.post(`/group-invites/${iid}/decline`); toast.success("Declined"); loadInvites(); }
    catch { toast.error("Failed"); }
  };
  const cancelInvite = async (iid) => {
    try { await api.delete(`/group-invites/${iid}`); toast.success("Cancelled"); loadInvites(); }
    catch { toast.error("Failed"); }
  };

  const togglePick = (f) => {
    setPickedFriends((prev) => prev.includes(f.id) ? prev.filter((id) => id !== f.id) : [...prev, f.id]);
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: g } = await api.post("/groups", { name, emoji, group_type: type, currency });
      // Add picked friends in parallel (best-effort)
      if (pickedFriends.length) {
        await Promise.allSettled(
          pickedFriends.map((uid) => api.post(`/groups/${g.id}/members/by-id`, { user_id: uid }))
        );
      }
      toast.success(`Group ${name} created${pickedFriends.length ? ` with ${pickedFriends.length} friend${pickedFriends.length === 1 ? "" : "s"}` : ""}`);
      setOpen(false);
      setName(""); setEmoji("🍕"); setType("custom"); setCurrency("USD"); setPickedFriends([]);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create group");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar onOpenCmdK={() => setCmdkOpen(true)} />
      <CommandPalette open={cmdkOpen} setOpen={setCmdkOpen} />

      <main className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/40 mb-2">/ Groups</div>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter">Your squads</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setJoinOpen(true)}
              className="border-border bg-foreground/[0.02] hover:bg-foreground/[0.05] h-11"
              data-testid="groups-join-btn"
            >
              <LogIn className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Join a group
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="brand-bg text-black hover:opacity-90 h-11" data-testid="groups-new-btn">
                  <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> New group
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">Create group</DialogTitle>
                </DialogHeader>
                <form onSubmit={create} className="space-y-4" data-testid="new-group-form">
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/50">Name</Label>
                    <Input required value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 bg-muted border-border text-base" placeholder="Trip to Goa" data-testid="new-group-name" />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/50">Cover emoji</Label>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {EMOJIS.map((e) => (
                        <button
                          key={e} type="button" onClick={() => setEmoji(e)}
                          className={`h-11 w-11 rounded-md text-lg transition ${emoji === e ? "bg-foreground/10 ring-1 ring-[var(--brand)]" : "bg-foreground/[0.03] hover:bg-foreground/10"}`}
                          data-testid={`emoji-${e}`}
                        >{e}</button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/50">Type</Label>
                      <Select value={type} onValueChange={setType}>
                        <SelectTrigger className="mt-1.5 bg-muted border-border h-11 text-base" data-testid="new-group-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {GROUP_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>{t.emoji} {t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/50">Currency</Label>
                      <Select value={currency} onValueChange={setCurrency}>
                        <SelectTrigger className="mt-1.5 bg-muted border-border h-11 text-base" data-testid="new-group-currency">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {CURRENCIES.map((c) => (
                            <SelectItem key={c.value} value={c.value}>{c.symbol} {c.value}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="pt-3 border-t border-border">
                    <Label className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/50 mb-2 block">
                      Add friends now <span className="text-foreground/30">(optional)</span>
                    </Label>
                    <FriendPicker mode="pick" picked={pickedFriends} onTogglePick={togglePick} />
                    {pickedFriends.length > 0 && (
                      <p className="text-[11px] font-mono text-muted-foreground mt-2" data-testid="new-group-picked-count">
                        {pickedFriends.length} friend{pickedFriends.length === 1 ? "" : "s"} will be added
                      </p>
                    )}
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={saving} className="brand-bg text-black hover:opacity-90 w-full h-11" data-testid="new-group-submit">
                      {saving ? "Creating…" : "Create group"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Pending group invites */}
        {(invites.incoming.length > 0 || invites.outgoing.length > 0) && (
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.03] p-6 mb-8" data-testid="group-invites-panel">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-amber-300 mb-4 flex items-center gap-2">
              <Mailbox className="h-3.5 w-3.5" strokeWidth={1.5} /> Group invites
              <span className="text-muted-foreground">· {invites.incoming.length + invites.outgoing.length}</span>
            </div>
            {invites.incoming.length > 0 && (
              <div className={invites.outgoing.length > 0 ? "mb-4" : ""}>
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Incoming</div>
                <div className="divide-y divide-border">
                  {invites.incoming.map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between py-3 gap-3" data-testid={`incoming-invite-${inv.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="h-10 w-10 rounded-xl bg-foreground/5 flex items-center justify-center text-xl shrink-0">{inv.group_emoji}</span>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{inv.group_name}</div>
                          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                            {inv.from_user_name} · {inv.group_type} · {inv.member_count} members
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button size="sm" onClick={() => acceptInvite(inv.id)} className="brand-bg text-black hover:opacity-90 h-8" data-testid={`accept-invite-${inv.id}`}>
                          <Check className="h-3.5 w-3.5 mr-1" strokeWidth={2} /> Join
                        </Button>
                        <button onClick={() => declineInvite(inv.id)} className="h-8 w-8 rounded-md border border-border hover:bg-foreground/5 transition flex items-center justify-center" data-testid={`decline-invite-${inv.id}`} aria-label="Decline">
                          <X className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {invites.outgoing.length > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Sent by you</div>
                <div className="divide-y divide-border">
                  {invites.outgoing.map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between py-3 gap-3" data-testid={`outgoing-invite-${inv.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="h-9 w-9 rounded-xl bg-foreground/5 flex items-center justify-center text-lg shrink-0">{inv.group_emoji}</span>
                        <div className="min-w-0">
                          <div className="text-sm truncate">{inv.to_user_name} → {inv.group_name}</div>
                          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" strokeWidth={1.5} /> pending · 7-day expiry</div>
                        </div>
                      </div>
                      <button onClick={() => cancelInvite(inv.id)} className="text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-red-400 h-8 px-3 rounded-md border border-border" data-testid={`cancel-invite-${inv.id}`}>
                        Cancel
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-foreground/40 text-sm">Loading…</div>
        ) : groups.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-16 text-center" data-testid="groups-empty-state">
            <div className="h-14 w-14 mx-auto rounded-2xl bg-foreground/5 border border-border flex items-center justify-center mb-4">
              <Users className="h-6 w-6 text-foreground/40" strokeWidth={1.5} />
            </div>
            <h3 className="font-heading text-2xl tracking-tight mb-2">No groups yet</h3>
            <p className="text-foreground/50 mb-6 max-w-sm mx-auto">Groups are where bills live — roommates, trips, dinners, or your softball team.</p>
            <Button onClick={() => setOpen(true)} className="brand-bg text-black hover:opacity-90" data-testid="empty-create-group">
              <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Create your first group
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {groups.map((g) => (
              <Link
                key={g.id} to={`/groups/${g.id}`}
                className="group rounded-2xl border border-border bg-card/40 p-6 hover:border-border transition"
                data-testid={`group-list-${g.id}`}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="h-12 w-12 rounded-xl bg-foreground/5 flex items-center justify-center text-2xl">{g.emoji}</div>
                  <ArrowUpRight className="h-5 w-5 text-foreground/30 group-hover:text-white transition" strokeWidth={1.5} />
                </div>
                <div className="font-heading text-xl tracking-tight mb-1 truncate">{g.name}</div>
                <div className="text-xs font-mono uppercase tracking-wider text-foreground/40 mb-4">
                  {g.group_type} · {g.currency}
                </div>
                <div className="flex items-center justify-between text-xs text-foreground/50">
                  <div className="flex -space-x-2">
                    {g.members.slice(0, 4).map((m) => (
                      <div key={m.id} className="h-7 w-7 rounded-full bg-foreground/10 border-2 border-card flex items-center justify-center text-[11px]">
                        {m.avatar_emoji || "🙂"}
                      </div>
                    ))}
                    {g.members.length > 4 && (
                      <div className="h-7 w-7 rounded-full bg-foreground/10 border-2 border-card flex items-center justify-center text-[10px] font-mono">
                        +{g.members.length - 4}
                      </div>
                    )}
                  </div>
                  <span className="font-mono uppercase tracking-wider">
                    {g.bill_count} bills
                  </span>
                </div>
                <div className="mt-4 text-[10px] font-mono uppercase tracking-[0.2em] text-foreground/30">
                  Updated {formatDistanceToNowStrict(new Date(g.last_activity), { addSuffix: true })}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <JoinByCodeDialog open={joinOpen} onOpenChange={setJoinOpen} onJoined={load} />
    </div>
  );
}
